import React, { useMemo, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "./ui/command";
import { Check, ChevronsUpDown, MapPin, X } from "lucide-react";

// Canonical list — 28 states + 8 UTs of India (Govt of India, 2024).
const STATES = [
    "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh",
    "Goa", "Gujarat", "Haryana", "Himachal Pradesh", "Jharkhand",
    "Karnataka", "Kerala", "Madhya Pradesh", "Maharashtra", "Manipur",
    "Meghalaya", "Mizoram", "Nagaland", "Odisha", "Punjab",
    "Rajasthan", "Sikkim", "Tamil Nadu", "Telangana", "Tripura",
    "Uttar Pradesh", "Uttarakhand", "West Bengal",
];
const UNION_TERRITORIES = [
    "Andaman & Nicobar Islands", "Chandigarh",
    "Dadra & Nagar Haveli and Daman & Diu", "Delhi",
    "Jammu & Kashmir", "Ladakh", "Lakshadweep", "Puducherry",
];

// Loose-match incoming territory strings to the canonical list so legacy
// admin-typed values like "asam" or " Assam " still round-trip cleanly.
const norm = (s) => (s || "").trim().toLowerCase().replace(/\s+/g, " ");
const CANON_BY_NORM = Object.fromEntries(
    [...STATES, ...UNION_TERRITORIES].map((s) => [norm(s), s]),
);

function parseTerritory(value) {
    if (!value) return [];
    return value
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean)
        .map((s) => CANON_BY_NORM[norm(s)] || s) // map mistyped names to the canonical form when we can
        .filter((s, i, arr) => arr.indexOf(s) === i); // de-dupe
}

/**
 * Multi-select picker for MR territories. Stores its value as a single comma-
 * separated string so `routes_mr.py · /mr/dues` (which already splits on `,`)
 * doesn't need any change.
 *
 * Props:
 *   value: "Assam, Meghalaya"        // current string
 *   onChange: (string) => void       // returns the cleaned, joined string
 *   placeholder: string              // optional
 *   testIdBase: string               // optional, used to build data-testids
 */
export default function TerritoryPicker({ value, onChange, placeholder = "Select states & UTs…", testIdBase = "territory" }) {
    const selected = useMemo(() => parseTerritory(value), [value]);
    const [open, setOpen] = useState(false);
    const selectedSet = useMemo(() => new Set(selected), [selected]);

    const commit = (next) => {
        const cleaned = next.filter((s, i, arr) => arr.indexOf(s) === i);
        onChange(cleaned.join(", "));
    };

    const toggle = (s) => {
        if (selectedSet.has(s)) commit(selected.filter((x) => x !== s));
        else commit([...selected, s]);
    };

    const removeOne = (s) => commit(selected.filter((x) => x !== s));
    const clearAll = () => commit([]);

    return (
        <div className="space-y-2" data-testid={testIdBase}>
            <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                    <button
                        type="button"
                        className="w-full flex items-center justify-between border border-stone-300 px-3 py-2 text-sm bg-white hover:border-sky-400 transition-colors"
                        data-testid={`${testIdBase}-trigger`}
                    >
                        <span className="flex items-center gap-2 text-stone-600">
                            <MapPin className="w-4 h-4 text-sky-700" />
                            {selected.length === 0 ? (
                                <span className="text-stone-400">{placeholder}</span>
                            ) : (
                                <span>{selected.length} {selected.length === 1 ? "state" : "states"} selected</span>
                            )}
                        </span>
                        <ChevronsUpDown className="w-4 h-4 text-stone-400" />
                    </button>
                </PopoverTrigger>
                <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0 rounded-none border-stone-300" align="start">
                    <Command>
                        <CommandInput placeholder="Search state or UT…" data-testid={`${testIdBase}-search`} />
                        <CommandList className="max-h-72">
                            <CommandEmpty>No matching state.</CommandEmpty>
                            <CommandGroup heading="States">
                                {STATES.map((s) => (
                                    <CommandItem
                                        key={s}
                                        value={s}
                                        onSelect={() => toggle(s)}
                                        className="cursor-pointer"
                                        data-testid={`${testIdBase}-opt-${s.toLowerCase().replace(/\s+/g, "-")}`}
                                    >
                                        <div className={`w-4 h-4 mr-2 border ${selectedSet.has(s) ? "bg-sky-700 border-sky-700" : "border-stone-300"} flex items-center justify-center`}>
                                            {selectedSet.has(s) && <Check className="w-3 h-3 text-white" />}
                                        </div>
                                        {s}
                                    </CommandItem>
                                ))}
                            </CommandGroup>
                            <CommandGroup heading="Union Territories">
                                {UNION_TERRITORIES.map((s) => (
                                    <CommandItem
                                        key={s}
                                        value={s}
                                        onSelect={() => toggle(s)}
                                        className="cursor-pointer"
                                        data-testid={`${testIdBase}-opt-${s.toLowerCase().replace(/\s+/g, "-").replace(/&/g, "and")}`}
                                    >
                                        <div className={`w-4 h-4 mr-2 border ${selectedSet.has(s) ? "bg-sky-700 border-sky-700" : "border-stone-300"} flex items-center justify-center`}>
                                            {selectedSet.has(s) && <Check className="w-3 h-3 text-white" />}
                                        </div>
                                        {s}
                                    </CommandItem>
                                ))}
                            </CommandGroup>
                        </CommandList>
                    </Command>
                </PopoverContent>
            </Popover>

            {selected.length > 0 && (
                <div className="flex flex-wrap gap-1.5" data-testid={`${testIdBase}-chips`}>
                    {selected.map((s) => (
                        <span
                            key={s}
                            className="inline-flex items-center gap-1 bg-sky-100 text-sky-800 border border-sky-200 px-2 py-1 text-xs"
                            data-testid={`${testIdBase}-chip-${s.toLowerCase().replace(/\s+/g, "-").replace(/&/g, "and")}`}
                        >
                            {s}
                            <button
                                type="button"
                                onClick={() => removeOne(s)}
                                className="hover:text-red-700"
                                aria-label={`Remove ${s}`}
                                data-testid={`${testIdBase}-remove-${s.toLowerCase().replace(/\s+/g, "-").replace(/&/g, "and")}`}
                            >
                                <X className="w-3 h-3" />
                            </button>
                        </span>
                    ))}
                    <button
                        type="button"
                        onClick={clearAll}
                        className="text-[11px] text-stone-500 hover:text-red-700 underline ml-1"
                        data-testid={`${testIdBase}-clear`}
                    >
                        Clear all
                    </button>
                </div>
            )}
        </div>
    );
}
